import {type ServiceModuleModel, ServiceModuleType} from "@/api/model";
import {useMemo, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {
    getGetApiCompaniesCurrentServicemodulesQueryKey,
    useGetApiCompaniesCurrentServicemodulesServiceModuleIdDescendants, usePatchApiCompaniesCurrentServicemodulesParent
} from "@/api/generated/company-management-user/company-management-user.ts";
import {notify} from "@/lib/notify.ts";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/shadcn/dialog.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/shadcn/popover.tsx";
import {Button} from "@/components/shadcn/button.tsx";
import {cn} from "@/lib/utils.ts";
import {Filter, Home, MapPin} from "lucide-react";
import {FolderTree} from "@/pages/service-modules/move-components/FolderTree.tsx";
import {useTranslation} from "react-i18next";

type Props = {
    module: ServiceModuleModel
    modules: ServiceModuleModel[]
    onClose: () => void
}

export function MoveServiceModuleDialog({ module, modules, onClose }: Props) {
    const { t } = useTranslation(['service-modules', 'common'])

    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)
    const [showAll, setShowAll] = useState(false)
    const queryClient = useQueryClient()

    const { data: descendantsData } = useGetApiCompaniesCurrentServicemodulesServiceModuleIdDescendants(module.id)

    const disabledIds = useMemo(() => {
        const ids = new Set<number>()
        ids.add(module.id)
        descendantsData?.data?.forEach(d => ids.add(d.id))
        return ids
    }, [descendantsData, module.id])

    const visibleModules = showAll
        ? modules
        : modules.filter(m => m.type === ServiceModuleType.Folder)

    const { mutate: moveModule, isPending } = usePatchApiCompaniesCurrentServicemodulesParent({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey() })
                onClose()
            },
            onError: (error) => notify.apiError(error),
        },
    })

    function handleSave() {
        moveModule({
            params: {
                serviceModuleId: module.id,
                folderServiceModuleId: selectedFolderId ?? undefined,
            },
        })
    }

    function buildPath(targetId: number | null, allModules: ServiceModuleModel[]): string {
        if (targetId === null) return t('moveDialog.root')
        const byId = new Map(allModules.map(m => [m.id, m]))
        const parts: string[] = []
        let id: number | null = targetId
        while (id != null) {
            const m = byId.get(id)
            if (!m) break
            parts.unshift(m.name ?? '—')
            id = m.parentFolderServiceModuleId ?? null
        }
        return t('moveDialog.root') + ' / ' + parts.join(' / ')
    }

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-h-[600px] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('moveDialog.title', { name: module.name })}</DialogTitle>
                </DialogHeader>

                <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('moveDialog.selectTarget')}</span>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className={cn('size-7', showAll && 'text-primary')}
                            >
                                <Filter className="size-3.5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48 p-2" align="end">
                            <label className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 rounded hover:bg-muted">
                                <input
                                    type="checkbox"
                                    checked={showAll}
                                    onChange={(e) => setShowAll(e.target.checked)}
                                />
                                {t('moveDialog.showAllModules')}
                            </label>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="border rounded-lg overflow-y-auto flex-1 min-h-0">
                    <button
                        disabled={module.parentFolderServiceModuleId === null}
                        className={cn(
                            'w-full text-left text-sm px-2 py-1.5 flex items-center gap-2',
                            module.parentFolderServiceModuleId !== null && 'hover:bg-muted cursor-pointer',
                            module.parentFolderServiceModuleId === null && 'opacity-40 cursor-default',
                            selectedFolderId === null && 'bg-primary/10 text-primary font-medium ring-1 ring-primary/20'
                        )}
                        onClick={() => {
                            if (module.parentFolderServiceModuleId === null) return
                            setSelectedFolderId(null)
                        }}
                    >
                        <Home className="size-4 shrink-0 text-muted-foreground" />
                        {t('moveDialog.root')}
                    </button>

                    <FolderTree
                        folders={visibleModules}
                        parentId={null}
                        depth={0}
                        selected={selectedFolderId}
                        onSelect={(id) => setSelectedFolderId(id)}
                        disabledIds={disabledIds}
                    />
                </div>

                <div className="flex items-start gap-2 px-3 py-2 bg-muted/50 rounded-lg text-sm">
                    <MapPin className="size-3.5 shrink-0 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground break-words">
                        {buildPath(selectedFolderId, modules)}
                    </span>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isPending}>
                        {t('buttons.cancel', { ns: 'common' })}
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            isPending ||
                            selectedFolderId === module.parentFolderServiceModuleId ||
                            (selectedFolderId != null && disabledIds.has(selectedFolderId))
                        }
                    >
                        {isPending ? t('buttons.saving', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}